// Pricing & promotions (spec/13 §18; F-PRC-01, F-PRC-10, F-POS-02, F-POS-03, F-POS-06, F-MEM-01): what
// a venue charges and what it takes off, under one sidebar item in Setup (the venue) and Commerce (the
// organisation). It replaced the "Catalogue" placeholder, renamed so it is not mistaken for HQ's game
// catalogue (the product owner, 2026-09-29). Four tabs, each its own URL (`…/pricing/{tab}`):
//
//   - Passes      (venue only) the pass catalogue, sell and grant — the screen that was `…/passes`
//   - Pricing     the pay-as-you-go rate and its session money settings, as one card
//   - Promotions  automatic promotions, discount codes and coupon batches
//   - Memberships not built yet (F-MEM): a short, honest placeholder
//
// Passes are sold at a venue, so the organisation's area has no Passes tab and opens on Pricing.

import { useTranslation } from '@clavian/i18n/react';
import { Tabs, type TabItem } from '@clavian/ui-web';
import { Navigate, useNavigate, useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useConsoleScope } from '../../shell/scope';
import { Page } from '../../ui';
import { PassesTab } from '../passes/PassesTab';
import { MembershipsTab } from './MembershipsTab';
import { PricingHero } from './PricingHero';
import { PaygPricingTab } from './PaygPricingTab';
import { PromotionsTab } from './PromotionsTab';

import './pricing.css';

export const VENUE_PRICING_TABS = ['passes', 'payg', 'promotions', 'memberships'] as const;
export const ORGANIZATION_PRICING_TABS = ['payg', 'promotions', 'memberships'] as const;

export function PricingPage(): ReactNode {
  const { t } = useTranslation('console');
  const scope = useConsoleScope();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { readonly tab?: string; readonly venueSlug?: string; readonly orgSlug?: string };
  const atVenue = scope.kind === 'venue';
  const tabs: readonly string[] = atVenue ? VENUE_PRICING_TABS : ORGANIZATION_PRICING_TABS;
  const first = tabs[0] ?? 'payg';

  // An unknown tab — or Passes at the organisation — opens the section's first tab instead.
  if (params.tab === undefined || !tabs.includes(params.tab)) {
    return atVenue ? (
      <Navigate to="/v/$venueSlug/pricing/$tab" params={{ venueSlug: scope.venue.slug, tab: first }} replace />
    ) : (
      <Navigate to="/o/$orgSlug/pricing/$tab" params={{ orgSlug: scope.organization.slug, tab: first }} replace />
    );
  }

  const open = (tab: string): void => {
    if (atVenue) void navigate({ to: '/v/$venueSlug/pricing/$tab', params: { venueSlug: scope.venue.slug, tab } });
    else void navigate({ to: '/o/$orgSlug/pricing/$tab', params: { orgSlug: scope.organization.slug, tab } });
  };

  const content: Record<string, ReactNode> = {
    passes: atVenue ? <PassesTab /> : null,
    payg: <PaygPricingTab />,
    promotions: <PromotionsTab />,
    memberships: <MembershipsTab />,
  };
  const items: TabItem[] = tabs.map((tab) => ({
    value: tab,
    label: tab === 'memberships' ? <MembershipsLabel /> : t(`pricing.tabs.${tab}`),
    content: content[tab],
  }));

  return (
    <Page title={t('pricing.title')} description={atVenue ? t('pricing.intro') : t('pricing.introOrganization')} hero={{ stats: <PricingHero venueId={atVenue ? scope.venue.id : undefined} /> }}>
      <Tabs label={t('pricing.tabs.label')} items={items} value={params.tab} onValueChange={open} className="console-page-tabs console-pricing-tabs" />
    </Page>
  );
}

/** The Memberships tab's label, with the quiet "Soon" the sidebar uses for an unbuilt section. */
function MembershipsLabel(): ReactNode {
  const { t } = useTranslation('console');
  return (
    <span className="console-pricing-tab-label">
      {t('pricing.tabs.memberships')}
      <span className="console-pricing-soon">{t('shell.soon')}</span>
    </span>
  );
}
